import React from 'react';
import { Paper, Typography, Box, Chip, Avatar } from '@mui/material';
import { Phone, Email, LinkedIn, LocationOn, Star, Work, School, Code } from '@mui/icons-material';
import MarkdownRenderer from '../components/MarkdownRenderer';

const CreativeTemplate = ({ 
  resumeData, 
  customization = {},
  paperSize = 'A4'
}) => {
  const {
    primaryColor = '#e91e63',
    secondaryColor = '#9c27b0',
    accentColor = '#ff5722',
    fontFamily = 'Roboto',
    fontSize = 12,
    spacing = 'normal'
  } = customization;

  const spacingMap = {
    compact: 0.5,
    normal: 1,
    spacious: 1.5
  };

  const currentSpacing = spacingMap[spacing] || 1;

  // Paper dimensions based on size
  const paperDimensions = {
    A4: { width: '210mm', height: '297mm' },
    Letter: { width: '8.5in', height: '11in' },
    Legal: { width: '8.5in', height: '14in' },
    A3: { width: '297mm', height: '420mm' }
  };

  const styles = {
    container: {
      width: paperDimensions[paperSize].width,
      height: paperDimensions[paperSize].height,
      fontFamily: fontFamily,
      fontSize: `${fontSize}px`,
      lineHeight: 1.4,
      margin: '0 auto',
      backgroundColor: 'white',
      boxShadow: '0 0 20px rgba(0,0,0,0.1)',
      position: 'relative',
      overflow: 'hidden',
      '@media print': {
        boxShadow: 'none',
        margin: 0
      }
    },
    backgroundPattern: {
      position: 'absolute',
      top: 0,
      right: 0,
      width: '200px',
      height: '200px',
      background: `linear-gradient(45deg, ${primaryColor}20, ${secondaryColor}20)`,
      borderRadius: '50%',
      transform: 'translate(50%, -50%)',
      zIndex: 0
    },
    content: {
      position: 'relative',
      zIndex: 1,
      padding: `${20 * currentSpacing}px`
    },
    header: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: `${20 * currentSpacing}px`,
      marginBottom: `${30 * currentSpacing}px`,
      background: `linear-gradient(135deg, ${primaryColor}15, ${secondaryColor}15)`,
      padding: `${20 * currentSpacing}px`,
      borderRadius: '15px',
      border: `2px solid ${primaryColor}30`
    },
    profileSection: {
      flex: 1
    },
    avatar: {
      width: `${80 * currentSpacing}px`,
      height: `${80 * currentSpacing}px`,
      border: `4px solid ${primaryColor}`,
      fontSize: `${fontSize + 20}px`,
      backgroundColor: primaryColor,
      color: 'white'
    },
    name: {
      fontSize: `${fontSize + 10}px`,
      fontWeight: 'bold',
      background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      marginBottom: `${8 * currentSpacing}px`
    },
    title: {
      fontSize: `${fontSize + 4}px`,
      color: accentColor,
      fontWeight: '600',
      marginBottom: `${16 * currentSpacing}px`
    },
    contactInfo: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: `${12 * currentSpacing}px`,
      fontSize: `${fontSize - 1}px`
    },
    contactItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      color: '#555',
      padding: '4px',
      borderRadius: '8px',
      transition: 'all 0.3s ease'
    },
    sectionHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: `${12 * currentSpacing}px`,
      marginBottom: `${16 * currentSpacing}px`,
      marginTop: `${24 * currentSpacing}px`
    },
    sectionIcon: {
      backgroundColor: primaryColor,
      color: 'white',
      padding: '8px',
      borderRadius: '50%',
      fontSize: `${fontSize + 4}px`
    },
    sectionTitle: {
      fontSize: `${fontSize + 4}px`,
      fontWeight: 'bold',
      color: primaryColor,
      textTransform: 'uppercase',
      letterSpacing: '1px'
    },
    timelineContainer: {
      position: 'relative',
      paddingLeft: `${30 * currentSpacing}px`
    },
    timelineLine: {
      position: 'absolute',
      left: '15px',
      top: 0,
      bottom: 0,
      width: '3px',
      background: `linear-gradient(180deg, ${primaryColor}, ${secondaryColor})`,
      borderRadius: '2px'
    },
    timelineItem: {
      position: 'relative',
      marginBottom: `${20 * currentSpacing}px`,
      backgroundColor: '#f9f9f9',
      padding: `${16 * currentSpacing}px`,
      borderRadius: '12px',
      border: `1px solid ${primaryColor}20`,
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
    },
    timelineDot: {
      position: 'absolute',
      left: `-${22 * currentSpacing}px`,
      top: `${16 * currentSpacing}px`,
      width: '12px',
      height: '12px',
      backgroundColor: primaryColor,
      borderRadius: '50%',
      border: '3px solid white',
      boxShadow: '0 0 0 2px ' + primaryColor
    },
    itemHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: `${12 * currentSpacing}px`,
      flexWrap: 'wrap'
    },
    jobTitle: {
      fontSize: `${fontSize + 2}px`,
      fontWeight: 'bold',
      color: '#333',
      marginBottom: '4px'
    },
    company: {
      fontSize: `${fontSize + 1}px`,
      color: primaryColor,
      fontWeight: '600'
    },
    duration: {
      fontSize: `${fontSize - 1}px`,
      color: '#666',
      backgroundColor: accentColor + '20',
      padding: '4px 8px',
      borderRadius: '12px',
      fontWeight: '500'
    },
    description: {
      fontSize: `${fontSize}px`,
      color: '#555',
      lineHeight: 1.6,
      marginBottom: `${12 * currentSpacing}px`
    },
    skillsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
      gap: `${12 * currentSpacing}px`,
      marginTop: `${16 * currentSpacing}px`
    },
    skillCard: {
      backgroundColor: '#f8f9fa',
      padding: `${12 * currentSpacing}px`,
      borderRadius: '10px',
      textAlign: 'center',
      border: `2px solid ${primaryColor}20`,
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        borderColor: primaryColor
      }
    },
    skillName: {
      fontSize: `${fontSize}px`,
      fontWeight: '600',
      color: '#333'
    },
    skillLevel: {
      display: 'flex',
      justifyContent: 'center',
      gap: '2px',
      marginTop: '4px'
    },
    summary: {
      fontSize: `${fontSize + 1}px`,
      lineHeight: 1.6,
      color: '#555',
      backgroundColor: '#f8f9fa',
      padding: `${16 * currentSpacing}px`,
      borderRadius: '12px',
      border: `2px solid ${primaryColor}20`,
      marginBottom: `${20 * currentSpacing}px`,
      position: 'relative'
    },
    summaryQuote: {
      position: 'absolute',
      top: '-10px',
      left: '20px',
      fontSize: '24px',
      color: primaryColor,
      backgroundColor: 'white',
      padding: '0 8px'
    },
    statsContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
      gap: `${16 * currentSpacing}px`,
      marginTop: `${20 * currentSpacing}px`,
      marginBottom: `${20 * currentSpacing}px`
    },
    statCard: {
      textAlign: 'center',
      padding: `${16 * currentSpacing}px`,
      backgroundColor: primaryColor + '10',
      borderRadius: '12px',
      border: `2px solid ${primaryColor}30`
    },
    statNumber: {
      fontSize: `${fontSize + 6}px`,
      fontWeight: 'bold',
      color: primaryColor,
      display: 'block'
    },
    statLabel: {
      fontSize: `${fontSize - 1}px`,
      color: '#666',
      marginTop: '4px'
    }
  };

  const formatDuration = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : new Date();
    const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    
    if (years === 0) return `${remainingMonths}mo`;
    if (remainingMonths === 0) return `${years}yr`;
    return `${years}yr ${remainingMonths}mo`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  const getTotalExperience = () => {
    if (!resumeData.experience || resumeData.experience.length === 0) return 0;
    
    let totalMonths = 0;
    resumeData.experience.forEach(exp => {
      const start = new Date(exp.startDate);
      const end = exp.endDate ? new Date(exp.endDate) : new Date();
      totalMonths += (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
    });
    
    return Math.floor(totalMonths / 12);
  };

  const getInitials = () => {
    const firstName = resumeData.personalInfo?.firstName || '';
    const lastName = resumeData.personalInfo?.lastName || '';
    return (firstName.charAt(0) + lastName.charAt(0)).toUpperCase();
  };

  const renderStars = (level = 3) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Star
        key={index}
        sx={{
          fontSize: '12px',
          color: index < level ? accentColor : '#ddd'
        }}
      />
    ));
  };

  return (
    <Paper sx={styles.container} elevation={0}>
      <Box sx={styles.backgroundPattern} />
      
      <Box sx={styles.content}>
        {/* Header Section */}
        <Box sx={styles.header}>
          <Avatar sx={styles.avatar}>
            {getInitials()}
          </Avatar>
          
          <Box sx={styles.profileSection}>
            <Typography sx={styles.name}>
              {resumeData.personalInfo?.firstName} {resumeData.personalInfo?.lastName}
            </Typography>
            
            {resumeData.personalInfo?.title && (
              <Typography sx={styles.title}>
                {resumeData.personalInfo.title}
              </Typography>
            )}

            <Box sx={styles.contactInfo}>
              {resumeData.personalInfo?.phone && (
                <Box sx={styles.contactItem}>
                  <Phone sx={{ fontSize: `${fontSize + 2}px`, color: primaryColor }} />
                  <span>{resumeData.personalInfo.phone}</span>
                </Box>
              )}
              {resumeData.personalInfo?.email && (
                <Box sx={styles.contactItem}>
                  <Email sx={{ fontSize: `${fontSize + 2}px`, color: primaryColor }} />
                  <span>{resumeData.personalInfo.email}</span>
                </Box>
              )}
              {resumeData.personalInfo?.linkedinUrl && (
                <Box sx={styles.contactItem}>
                  <LinkedIn sx={{ fontSize: `${fontSize + 2}px`, color: primaryColor }} />
                  <span>LinkedIn</span>
                </Box>
              )}
              {resumeData.personalInfo?.location && (
                <Box sx={styles.contactItem}>
                  <LocationOn sx={{ fontSize: `${fontSize + 2}px`, color: primaryColor }} />
                  <span>{resumeData.personalInfo.location}</span>
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        {/* Stats Cards */}
        <Box sx={styles.statsContainer}>
          <Box sx={styles.statCard}>
            <Typography sx={styles.statNumber}>{getTotalExperience()}+</Typography>
            <Typography sx={styles.statLabel}>Years Experience</Typography>
          </Box>
          <Box sx={styles.statCard}>
            <Typography sx={styles.statNumber}>{resumeData.experience?.length || 0}</Typography>
            <Typography sx={styles.statLabel}>Companies</Typography>
          </Box>
          <Box sx={styles.statCard}>
            <Typography sx={styles.statNumber}>{resumeData.skills?.length || 0}</Typography>
            <Typography sx={styles.statLabel}>Skills</Typography>
          </Box>
        </Box>

        {/* Professional Summary */}
        {resumeData.personalInfo?.summary && (
          <>
            <Box sx={styles.sectionHeader}>
              <Box sx={styles.sectionIcon}>
                <Star />
              </Box>
              <Typography sx={styles.sectionTitle}>About Me</Typography>
            </Box>
            <Box sx={styles.summary}>
              <Typography sx={styles.summaryQuote}>"</Typography>
              <MarkdownRenderer
                content={resumeData.personalInfo.summary}
                typography={{ fontSize: `${fontSize + 1}px`, lineHeight: 1.6, color: '#555' }}
              />
            </Box>
          </>
        )}

        {/* Experience Section */}
        {resumeData.experience && resumeData.experience.length > 0 && (
          <>
            <Box sx={styles.sectionHeader}>
              <Box sx={styles.sectionIcon}>
                <Work />
              </Box>
              <Typography sx={styles.sectionTitle}>Work Experience</Typography>
            </Box>
            
            <Box sx={styles.timelineContainer}>
              <Box sx={styles.timelineLine} />
              {resumeData.experience.map((exp, index) => (
                <Box key={index} sx={styles.timelineItem}>
                  <Box sx={styles.timelineDot} />
                  
                  <Box sx={styles.itemHeader}>
                    <Box>
                      <Typography sx={styles.jobTitle}>{exp.title}</Typography>
                      <Typography sx={styles.company}>{exp.company}</Typography>
                    </Box>
                    <Typography sx={styles.duration}>
                      {formatDate(exp.startDate)} - {exp.endDate ? formatDate(exp.endDate) : 'Present'}
                      <br />({formatDuration(exp.startDate, exp.endDate)})
                    </Typography>
                  </Box>
                  
                  {exp.description && (
                    <MarkdownRenderer
                      content={exp.description}
                      typography={styles.description}
                    />
                  )}
                </Box>
              ))}
            </Box>
          </>
        )}

        {/* Education Section */}
        {resumeData.education && resumeData.education.length > 0 && (
          <>
            <Box sx={styles.sectionHeader}>
              <Box sx={styles.sectionIcon}>
                <School />
              </Box>
              <Typography sx={styles.sectionTitle}>Education</Typography>
            </Box>
            
            <Box sx={styles.timelineContainer}>
              <Box sx={styles.timelineLine} />
              {resumeData.education.map((edu, index) => (
                <Box key={index} sx={styles.timelineItem}>
                  <Box sx={styles.timelineDot} />
                  
                  <Box sx={styles.itemHeader}>
                    <Box>
                      <Typography sx={styles.jobTitle}>{edu.degree}</Typography>
                      <Typography sx={styles.company}>{edu.institution}</Typography>
                    </Box>
                    {edu.graduationYear && (
                      <Typography sx={styles.duration}>
                        {edu.graduationYear}
                      </Typography>
                    )}
                  </Box>
                  
                  {edu.grade && (
                    <Typography sx={styles.description}>
                      Grade: {edu.grade}
                    </Typography>
                  )}
                  {edu.description && (
                    <MarkdownRenderer
                      content={edu.description}
                      typography={styles.description}
                    />
                  )}
                </Box>
              ))}
            </Box>
          </>
        )}

        {/* Skills Section */}
        {resumeData.skills && resumeData.skills.length > 0 && (
          <>
            <Box sx={styles.sectionHeader}>
              <Box sx={styles.sectionIcon}>
                <Code />
              </Box>
              <Typography sx={styles.sectionTitle}>Skills & Expertise</Typography>
            </Box>
            
            <Box sx={styles.skillsGrid}>
              {resumeData.skills.map((skill, index) => (
                <Box key={index} sx={styles.skillCard}>
                  <Typography sx={styles.skillName}>
                    {typeof skill === 'object' && skill !== null ? (typeof skill.name === 'string' ? skill.name : (skill.name ? String(skill.name) : (skill.id ? String(skill.id) : '[Skill]'))) : String(skill)}
                  </Typography>
                  <Box sx={styles.skillLevel}>
                    {renderStars(typeof skill === 'object' && skill !== null && skill.level ? skill.level : 3)}
                  </Box>
                </Box>
              ))}
            </Box>
          </>
        )}
      </Box>
    </Paper>
  );
};

export default CreativeTemplate;
